import { useEffect, useState, useCallback } from 'react'
import { RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2 } from 'lucide-react'
import api, { pesanGalat } from '../lib/api'

type Konfig = { sumber: string; dikonfigurasi: boolean; pesan: string }
type Penarikan = {
  sumber: string; wilayah: string; nama: string | null
  diambil_at: string; jml_entri: number; berhasil: boolean; galat: string | null
}
type Status = {
  ringkasan: string
  sumber: Konfig[]
  sumber_siap: number
  penarikan_terakhir: Penarikan[]
}
type Baris = {
  sumber: string; posisi: number; judul: string; artis: string
  posisi_lalu: number | null; gerak: number | null
  sudah_punya: boolean; status_want: string | null
}

const TGL = (s: string) =>
  new Date(s).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

export default function Chart({ bolehKelola }: { bolehKelola: boolean }) {
  const [status, setStatus] = useState<Status | null>(null)
  const [baris, setBaris] = useState<Baris[]>([])
  const [hanyaBelum, setHanyaBelum] = useState(true)
  const [sumber, setSumber] = useState('')
  const [memuat, setMemuat] = useState(false)
  const [menarik, setMenarik] = useState(false)
  const [galat, setGalat] = useState('')

  const muat = useCallback(async () => {
    setMemuat(true); setGalat('')
    try {
      const p = new URLSearchParams()
      if (hanyaBelum) p.set('belum', '1')
      if (sumber) p.set('sumber', sumber)
      const [s, c] = await Promise.all([
        api.get('/api/chart/status'),
        api.get(`/api/chart?${p}`),
      ])
      setStatus(s.data)
      setBaris(c.data ?? [])
    } catch (e) {
      // Galat DITAMPILKAN. Daftar kosong yang sebenarnya permintaan gagal
      // tidak bisa dibedakan dari "memang belum ada data" — dan yang
      // membacanya akan menyimpulkan chart-nya memang kosong.
      setGalat(pesanGalat(e, 'Gagal memuat chart'))
    } finally { setMemuat(false) }
  }, [hanyaBelum, sumber])

  useEffect(() => { muat() }, [muat])

  async function tarikSekarang() {
    setMenarik(true); setGalat('')
    try { await api.post('/api/chart/tarik'); await muat() }
    catch (e) { setGalat(pesanGalat(e, 'Gagal menarik chart')) }
    finally { setMenarik(false) }
  }

  // `?.` menjaga `status`, TIDAK menjaga isinya.
  //
  // Saat endpoint gagal, balasannya {error, pesan} — TANPA `sumber` dan TANPA
  // `penarikan_terakhir`. `status?.sumber` jadi undefined, dan .filter/.map di
  // atasnya meledak persis seperti bug workflow di Siapkan.tsx 25 Agu 2026.
  //
  // Dinormalkan SEKALI di sini, bukan dijaga berulang di tiap tempat pakai.
  //
  // Namanya `daftarSumber`, BUKAN `sumber`: `sumber` sudah dipakai state
  // penyaring di baris 29 (nama sumber yang sedang dipilih). Dua hal berbeda,
  // dan menamainya sama membuat TypeScript menolak seluruh berkas.
  // Ditulis `status && Array.isArray(...)`, bukan `Array.isArray(status?...)`.
  //
  // `Array.isArray(status?.sumber)` memang benar saat dijalankan, tapi TypeScript
  // TIDAK menyimpulkan dari situ bahwa `status` bukan null — jadi `status.sumber`
  // di cabang benarnya ditolak "possibly null". Menyebut `status &&` lebih dulu
  // membuat penyempitan tipenya terbaca kompiler, bukan cuma benar di kepala kita.
  const daftarSumber: Konfig[] =
    status && Array.isArray(status.sumber) ? status.sumber : []
  const penarikan: Penarikan[] =
    status && Array.isArray(status.penarikan_terakhir) ? status.penarikan_terakhir : []
  const belumSiap = daftarSumber.filter(s => !s.dikonfigurasi)

  return (
    <div className="space-y-5">
      {/* ── Keadaan sumber. Selalu tampil, bahkan (terutama) saat kosong ──
          Daftar kosong tanpa penjelasan tidak bisa dibedakan dari "kunci API
          belum diisi", dan yang membacanya akan menebak. */}
      <div className="bg-bg-1 border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13px] text-text-2">{status?.ringkasan ?? 'Memuat…'}</div>
          {bolehKelola && (
            <button onClick={tarikSekarang} disabled={menarik || (status?.sumber_siap ?? 0) === 0}
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-gold/10 border
                         border-gold/30 text-gold hover:bg-gold/20 transition-colors disabled:opacity-40">
              <RefreshCw size={13} className={menarik ? 'animate-spin' : ''} />
              {menarik ? 'Menarik…' : 'Tarik sekarang'}
            </button>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          {daftarSumber.map(s => {
            const p = penarikan.find(x => x.sumber === s.sumber)
            return (
              <div key={s.sumber} className="bg-bg-2 border border-border rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-2">
                  {s.dikonfigurasi
                    ? <CheckCircle2 size={14} className="text-gold shrink-0" />
                    : <AlertTriangle size={14} className="text-amber-400 shrink-0" />}
                  <span className="text-[13px] font-medium capitalize">{s.sumber}</span>
                  {p && (
                    <span className="ml-auto text-[11px] text-text-3">
                      {p.berhasil ? `${p.jml_entri} lagu · ${TGL(p.diambil_at)}` : 'penarikan terakhir GAGAL'}
                    </span>
                  )}
                </div>
                {!s.dikonfigurasi && <div className="text-[11px] text-text-3 mt-1.5 leading-relaxed">{s.pesan}</div>}
                {p && !p.berhasil && p.galat && (
                  <div className="text-[11px] text-red-400 mt-1.5 leading-relaxed">{p.galat}</div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {galat && (
        <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
          {galat}
        </div>
      )}

      {/* ── Daftar ── */}
      <div className="bg-bg-1 border border-border rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <select value={sumber} onChange={e => setSumber(e.target.value)}>
            <option value="">Semua sumber</option>
            <option value="spotify">Spotify</option>
            <option value="youtube">YouTube</option>
          </select>
          <label className="flex items-center gap-2 text-[12px] text-text-2 cursor-pointer select-none">
            <input type="checkbox" checked={hanyaBelum} onChange={e => setHanyaBelum(e.target.checked)}
              className="accent-[#d4af37]" />
            Hanya yang belum ada di katalog
          </label>
          <span className="text-[12px] text-text-3">{memuat ? 'memuat…' : `${baris.length} baris`}</span>
        </div>

        {baris.length === 0 && !memuat ? (
          <div className="text-[13px] text-text-3 py-10 text-center">
            {(status?.sumber_siap ?? 0) === 0
              ? 'Belum ada sumber chart yang dikonfigurasi — isi kunci API dulu.'
              : 'Belum ada data chart. Tekan "Tarik sekarang" atau tunggu jadwal harian.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-text-3">
                <tr className="border-b border-border">
                  <th className="text-left font-medium py-2 w-14">#</th>
                  <th className="text-left font-medium py-2">Judul</th>
                  <th className="text-left font-medium py-2">Artis</th>
                  <th className="text-left font-medium py-2 w-28">Gerak 7 hari</th>
                  <th className="text-left font-medium py-2 w-32">Katalog</th>
                </tr>
              </thead>
              <tbody>
                {baris.map((b, i) => (
                  <tr key={`${b.sumber}-${b.posisi}-${i}`} className="border-b border-border/60 hover:bg-bg-2/50">
                    <td className="py-2.5 text-text-3">{b.posisi}</td>
                    <td className="py-2.5 pr-3">{b.judul}</td>
                    <td className="py-2.5 pr-3 text-text-2">{b.artis}</td>
                    <td className="py-2.5"><Gerak nilai={b.gerak} /></td>
                    <td className="py-2.5">
                      {b.sudah_punya
                        ? <span className="text-[11px] px-2 py-0.5 rounded-md bg-gold/10 text-gold border border-gold/30">sudah punya</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 text-text-3 border border-border">
                            {b.status_want ?? 'belum'}
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Gerak posisi. NULL dibedakan dari nol — lagu yang baru terpantau BUKAN lagu
// yang tidak bergerak, dan menyamakannya menyembunyikan justru yang baru naik.
function Gerak({ nilai }: { nilai: number | null }) {
  if (nilai === null) return <span className="text-[11px] text-text-3">baru terpantau</span>
  if (nilai > 0) return <span className="flex items-center gap-1 text-[12px] text-emerald-400"><TrendingUp size={13} /> +{nilai}</span>
  if (nilai < 0) return <span className="flex items-center gap-1 text-[12px] text-red-400"><TrendingDown size={13} /> {nilai}</span>
  return <span className="flex items-center gap-1 text-[12px] text-text-3"><Minus size={13} /> tetap</span>
}
